/* Scroll Video Annotations
   Maps scroll position within a tall track onto a Shopify-hosted video's
   currentTime (frame-by-frame scrub), and toggles X/Y hotspot markers on
   and off as the playhead crosses each annotation's time range. */

(function () {
	'use strict';

	class ScrollVideoAnnotations extends HTMLElement {
		connectedCallback() {
			this.video = this.querySelector('video');
			this.track = this.querySelector('.svid__track');
			this.hotspots = Array.prototype.slice.call(this.querySelectorAll('.svid-hotspot'));
			if (!this.video || !this.track) return;

			// Smoothing factor: setting value 0..90 -> easing 0 (locked) .. ~0.9 (glide).
			const raw = parseFloat(this.getAttribute('data-smoothing'));
			this.ease = isNaN(raw) ? 0.7 : Math.min(0.9, Math.max(0, raw / 100));

			this.current = 0;        // smoothed playhead (seconds)
			this.target = 0;         // scroll-derived target (seconds)
			this.lastSet = -1;       // last value written to video.currentTime
			this.duration = 0;
			this.inView = false;
			this.rafId = null;

			this.prepareVideo();
			this.bindHotspots();
			this.setupDirections();

			this.onScroll = this.onScroll.bind(this);
			this.tick = this.tick.bind(this);

			if (this.video.readyState >= 1 && this.video.duration) {
				this.onMeta();
			} else {
				this.video.addEventListener('loadedmetadata', () => this.onMeta(), { once: true });
			}

			this.observe();
		}

		prepareVideo() {
			const v = this.video;
			v.removeAttribute('autoplay');
			v.removeAttribute('loop');
			v.muted = true;
			v.defaultMuted = true;
			v.setAttribute('muted', '');
			v.setAttribute('playsinline', '');
			v.setAttribute('webkit-playsinline', '');
			v.controls = false;
			v.preload = 'auto';
			// Nudge buffering on some browsers, then immediately pause/seek to start.
			const settle = () => { try { v.pause(); v.currentTime = 0; } catch (e) {} };
			v.addEventListener('canplay', () => {
				this.classList.add('is-ready');
				settle();
			}, { once: true });
			try { v.load(); } catch (e) {}
		}

		bindHotspots() {
			this.hotspots = this.hotspots.map((el) => {
				const start = parseFloat(el.getAttribute('data-start'));
				const endRaw = el.getAttribute('data-end');
				const end = endRaw === '' || endRaw === null ? Infinity : parseFloat(endRaw);
				return {
					el: el,
					start: isNaN(start) ? 0 : start,
					end: isNaN(end) ? Infinity : end,
					active: false
				};
			});
		}

		// Swap each hotspot's label-direction class when crossing the mobile breakpoint.
		setupDirections() {
			const DIRS = ['right', 'left', 'top', 'bottom'];
			this.mq = window.matchMedia('(max-width: 749px)');
			this.applyDirections = () => {
				const mobile = this.mq.matches;
				this.hotspots.forEach((h) => {
					const el = h.el;
					let dir = mobile ? (el.getAttribute('data-dir-mobile') || el.getAttribute('data-dir')) : el.getAttribute('data-dir');
					if (DIRS.indexOf(dir) === -1) dir = 'right';
					DIRS.forEach((d) => el.classList.toggle('svid-hotspot--' + d, d === dir));
				});
			};
			this.applyDirections();
			if (this.mq.addEventListener) {
				this.mq.addEventListener('change', this.applyDirections);
			} else if (this.mq.addListener) {
				this.mq.addListener(this.applyDirections);
			}
		}

		onMeta() {
			this.duration = this.video.duration || 0;
			// Resolve open-ended annotations to the real duration.
			this.hotspots.forEach((h) => {
				if (h.end === Infinity) h.end = this.duration;
			});
			this.updateTarget();
			this.current = this.target;
			this.seek(this.current, true);
			this.updateHotspots(this.current);
		}

		observe() {
			if ('IntersectionObserver' in window) {
				this.io = new IntersectionObserver((entries) => {
					this.inView = entries[0].isIntersecting;
					if (this.inView) this.start();
					else this.stop();
				}, { rootMargin: '0px', threshold: 0 });
				this.io.observe(this);
			} else {
				this.inView = true;
				this.start();
			}
		}

		start() {
			window.addEventListener('scroll', this.onScroll, { passive: true });
			window.addEventListener('resize', this.onScroll, { passive: true });
			this.updateTarget();
			if (!this.rafId) this.rafId = requestAnimationFrame(this.tick);
		}

		stop() {
			window.removeEventListener('scroll', this.onScroll);
			window.removeEventListener('resize', this.onScroll);
			if (this.rafId) {
				cancelAnimationFrame(this.rafId);
				this.rafId = null;
			}
		}

		onScroll() {
			this.updateTarget();
			if (!this.rafId && this.inView) this.rafId = requestAnimationFrame(this.tick);
		}

		// Scroll progress through the track -> target playback time.
		updateTarget() {
			const rect = this.track.getBoundingClientRect();
			const travel = rect.height - window.innerHeight;
			let p = travel > 0 ? (-rect.top) / travel : 0;
			if (p < 0) p = 0;
			if (p > 1) p = 1;
			this.progress = p;
			this.target = p * this.duration;
		}

		tick() {
			this.rafId = null;
			if (!this.duration) return;

			// Ease the smoothed playhead toward the scroll target.
			if (this.ease <= 0) {
				this.current = this.target;
			} else {
				this.current += (this.target - this.current) * (1 - this.ease);
				if (Math.abs(this.target - this.current) < 0.008) this.current = this.target;
			}

			this.seek(this.current, false);
			this.updateHotspots(this.current);

			// Keep animating until the playhead has caught up to the target.
			if (this.inView && Math.abs(this.target - this.current) > 0.008) {
				this.rafId = requestAnimationFrame(this.tick);
			}
		}

		seek(time, force) {
			if (!force && Math.abs(time - this.lastSet) < 0.02) return;
			// Avoid stacking seeks while one is still in flight.
			if (!force && this.video.seeking) return;
			const clamped = Math.min(this.duration, Math.max(0, time));
			try { this.video.currentTime = clamped; } catch (e) {}
			this.lastSet = clamped;
		}

		updateHotspots(time) {
			for (let i = 0; i < this.hotspots.length; i++) {
				const h = this.hotspots[i];
				const on = time >= h.start && time <= h.end;
				if (on !== h.active) {
					h.active = on;
					h.el.classList.toggle('is-active', on);
				}
			}
		}

		disconnectedCallback() {
			this.stop();
			if (this.io) this.io.disconnect();
			if (this.mq && this.applyDirections) {
				if (this.mq.removeEventListener) this.mq.removeEventListener('change', this.applyDirections);
				else if (this.mq.removeListener) this.mq.removeListener(this.applyDirections);
			}
		}
	}

	if (!customElements.get('scroll-video-annotations')) {
		customElements.define('scroll-video-annotations', ScrollVideoAnnotations);
	}
})();
